/**
 * What the platform has told this person, and a hook that keeps asking.
 *
 * == Why this polls ======================================================
 *
 * A notification is written by the outbox worker, which makes one pass every
 * thirty seconds. No amount of client cleverness beats that floor, so a
 * socket would buy nothing and cost a second auth path. This asks every
 * twenty seconds while the tab is visible, and immediately when it becomes
 * visible again — which is the case that actually matters, because somebody
 * coming back from a call wants today's work, not a minute-old page.
 *
 * `latestAt` is the one value a workspace watches. When the newest
 * notification's timestamp changes, work crossed to this person, and their
 * board is now out of date — so the shells re-read their lists on it. That
 * is the whole of "refresh the tables after the assignment notification".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from './api';

export type Notification = {
  id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  data: Record<string, unknown> | null;
  created_at: string;
  /** The caller is not cleared for resident detail; the words were withheld, not the row. */
  redacted: boolean;
};

export type NotificationFeed = {
  items: Notification[];
  unread: number;
  latest_at: string | null;
  names_visible: boolean;
};

export const notificationsApi = {
  list: (limit = 25) => apiRequest<NotificationFeed>('GET', `/notifications?limit=${limit}`),
  markRead: (id: string) => apiRequest<{ id: string }>('POST', `/notifications/${id}/read`),
  markAllRead: () => apiRequest<{ marked: number }>('POST', '/notifications/read-all'),
};

/**
 * How often to ask while the tab is in front: once per pass of the worker
 * (30s). It was 20s, and every visible tab in an office behind one IP polled
 * on its own clock -- the largest share of the requests that rate-limited
 * the office on 2026-10-01.
 */
const POLL_MS = 30_000;
/** Focus and visibilitychange fire together when a tab comes back; ask once. */
const MIN_GAP_MS = 5_000;

export type NotificationState = {
  feed: NotificationFeed | null;
  /** Changes exactly when something new arrived. Workspaces re-read their lists on it. */
  latestAt: string | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** True once the first answer came back — so the bell does not flash an empty state. */
  ready: boolean;
};

export function useNotifications(enabled = true): NotificationState {
  const [feed, setFeed] = useState<NotificationFeed | null>(null);
  const [ready, setReady] = useState(false);
  const live = useRef(true);
  const lastAsked = useRef(0);

  const refresh = useCallback(async () => {
    lastAsked.current = Date.now();
    try {
      const r = await notificationsApi.list();
      if (!live.current) return;
      if (r.success && r.data) setFeed(r.data);
    } catch {
      // A failed poll is not an error worth showing. The bell keeps whatever
      // it last knew rather than claiming the work went away.
    } finally {
      if (live.current) setReady(true);
    }
  }, []);

  useEffect(() => {
    live.current = true;
    if (!enabled) return () => { live.current = false; };
    refresh();
    const tick = window.setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastAsked.current >= MIN_GAP_MS) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      live.current = false;
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, refresh]);

  const markRead = useCallback(async (id: string) => {
    // Optimistic: the row is this person's own and the server cannot refuse
    // it for any reason they could act on. A failure is corrected by the
    // next poll, twenty seconds away.
    setFeed((f) => (f
      ? { ...f, items: f.items.map((n) => (n.id === id ? { ...n, is_read: true } : n)), unread: Math.max(0, f.unread - (f.items.find((n) => n.id === id)?.is_read ? 0 : 1)) }
      : f));
    try { await notificationsApi.markRead(id); } catch { /* next poll corrects it */ }
  }, []);

  const markAllRead = useCallback(async () => {
    setFeed((f) => (f ? { ...f, items: f.items.map((n) => ({ ...n, is_read: true })), unread: 0 } : f));
    try { await notificationsApi.markAllRead(); } catch { /* next poll corrects it */ }
  }, []);

  return { feed, latestAt: feed?.latest_at ?? null, refresh, markRead, markAllRead, ready };
}
